/**
 * AdminStatisticsDashboard — Enterprise-grade Service Statistics Report
 * for NECS admins. Pulls a single pivot payload from
 * GET /api/analytics/service-statistics and lays it out as:
 *
 *   [ Filter row · State / Region / City / Date-from / Date-to / Refresh ]
 *   [ KPI cards  · Properties · Buildings · Units · Regions · Cities …  ]
 *   [ Per-service breakdown table                                        ]
 *   [ State → Region → City pivot table with sortable columns            ]
 *   [ CSV export                                                         ]
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  BarChart3, Building2, MapPin, Home, Package, Sparkles, RefreshCw,
  Download, ClipboardList, AlertTriangle, Truck, ChevronUp, ChevronDown,
  Users, FileCheck, Flag, Calendar, DollarSign, GraduationCap, Activity,
} from 'lucide-react';
import { toast } from 'sonner';
import { US_STATES, normalizeState } from '../lib/usStates';

const API = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { key: 'day', label: 'Today' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
  { key: 'custom', label: 'Custom' },
];

const SPECIALTY_LABELS = {
  valet_trash: 'Valet Trash',
  bulk_removal: 'Bulk Removal',
  pressure_washing: 'Pressure Washing',
  window_cleaning: 'Window Cleaning',
  dumpster_treatment: 'Dumpster Treatment',
  trash_room_maintenance: 'Trash Room Maintenance',
  eco_friendly_amenities: 'Eco-Friendly Amenities',
};

function num(v) { return Number(v ?? 0).toLocaleString(); }

export default function AdminStatisticsDashboard({ regions = [], properties = [], hideBilling = false }) {
  const [period, setPeriod] = useState('month');
  const [state, setState] = useState('__ALL__');
  // Big City tab strip default — Dallas is the launch metro. Matches
  // the tab strip on Routes & Regions and every Operations drill-down.
  const [bigCity, setBigCity] = useState('Dallas');
  const [regionId, setRegionId] = useState('__ALL__');
  const [city, setCity] = useState('__ALL__');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [sortBy, setSortBy] = useState({ col: 'properties', dir: 'desc' });

  // ── Available filter values (from client-side data so UX is instant) ─
  // Full US catalog per CEO directive — every state is selectable, even
  // ones without seeded regions. The `seededStateSet` tracks which ones
  // currently resolve so the dropdown can put a bullet next to them.
  const availableStates = US_STATES;
  const seededStateSet = useMemo(() => {
    const s = new Set();
    regions.forEach((r) => { if (r.state) s.add(normalizeState(r.state)); });
    return s;
  }, [regions]);

  const availableRegions = useMemo(
    () => regions
      .filter((r) => state === '__ALL__' || normalizeState(r.state || 'TX') === state)
      .filter((r) => !bigCity || (r.big_city || 'Unassigned') === bigCity)
      .slice()
      .sort((a, b) => (a.region_code || 'zzz').localeCompare(b.region_code || 'zzz')),
    [regions, state, bigCity],
  );

  const availableCities = useMemo(() => {
    const set = new Set();
    availableRegions.forEach((r) => {
      if (regionId === '__ALL__' || r.region_id === regionId) {
        (r.cities || []).forEach((c) => set.add(c));
      }
    });
    return Array.from(set).sort();
  }, [availableRegions, regionId]);

  // ── Fetch pivot payload ─────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (state !== '__ALL__') q.set('state', state);
      if (bigCity) q.set('big_city', bigCity);
      if (regionId !== '__ALL__') q.set('region_id', regionId);
      if (city !== '__ALL__') q.set('city', city);
      if (dateFrom) q.set('from', dateFrom);
      if (dateTo) q.set('to', dateTo);
      // Live snapshot uses period param unless we're in custom mode
      const snapQuery = new URLSearchParams(q);
      if (period && period !== 'custom') snapQuery.set('period', period);
      const [statsRes, snapRes] = await Promise.all([
        fetch(`${API}/api/analytics/service-statistics?${q.toString()}`, { credentials: 'include' }),
        fetch(`${API}/api/analytics/live-snapshot?${snapQuery.toString()}`, { credentials: 'include' }),
      ]);
      if (!statsRes.ok) throw new Error(`Statistics HTTP ${statsRes.status}`);
      if (!snapRes.ok) throw new Error(`Snapshot HTTP ${snapRes.status}`);
      setData(await statsRes.json());
      setSnapshot(await snapRes.json());
    } catch (e) {
      toast.error(`Statistics load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [state, bigCity, regionId, city, dateFrom, dateTo, period]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Per CEO OODA Feb 2026 — don't default to "All states". Auto-pick the
  // first *seeded* state (one with regions in the catalog) on mount so
  // the region column populates immediately.
  useEffect(() => {
    if (state !== '__ALL__') return;
    if (seededStateSet.size === 0) return;
    const firstSeeded = availableStates.find((s) => seededStateSet.has(s));
    if (firstSeeded) setState(firstSeeded);
  }, [state, availableStates, seededStateSet]);

  // ── Sort helpers for pivot table ────────────────────────────────────
  const sortedRegions = useMemo(() => {
    const rows = data?.by_region || [];
    return rows.slice().sort((a, b) => {
      const av = a[sortBy.col] ?? 0;
      const bv = b[sortBy.col] ?? 0;
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortBy.dir === 'asc' ? av - bv : bv - av;
      }
      return sortBy.dir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [data, sortBy]);

  const toggleSort = (col) => {
    setSortBy((prev) => (prev.col === col
      ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { col, dir: 'desc' }));
  };

  const exportCsv = () => {
    if (!data) return;
    const escape = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const sections = [];
    // Sheet 1 · Regions
    sections.push(['# STATE · REGION PIVOT']);
    sections.push(['State', 'Region Code', 'Region', 'Properties', 'Buildings',
                   'Units', 'Valet Active', 'Specialty Active',
                   'Daily Reports', 'Violations', 'Safety Reports']);
    (data.by_region || []).forEach((r) => sections.push([
      r.state, r.region_code, r.region_name, r.properties, r.buildings, r.units,
      r.valet_active, r.specialty_active, r.daily_reports, r.violations, r.safety_reports,
    ]));
    sections.push([]);
    // Sheet 2 · Cities
    sections.push(['# CITY BREAKDOWN']);
    sections.push(['State', 'Region Code', 'City', 'Properties', 'Buildings',
                   'Units', 'Valet Active', 'Specialty Active',
                   'Daily Reports', 'Violations']);
    (data.by_city || []).forEach((c) => sections.push([
      c.state, c.region_code, c.city, c.properties, c.buildings, c.units,
      c.valet_active, c.specialty_active, c.daily_reports, c.violations,
    ]));
    sections.push([]);
    // Sheet 3 · Services
    sections.push(['# SERVICE COVERAGE']);
    sections.push(['Service', 'Active Properties', 'Requests / Schedules']);
    (data.by_service || []).forEach((s) => sections.push([
      s.label || s.service_type, s.active_properties, s.requests_count,
    ]));
    const csv = sections.map((row) => row.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `necs-service-statistics-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  const totals = data?.totals || {};
  const anyFilter = state !== '__ALL__' || regionId !== '__ALL__' || city !== '__ALL__' || dateFrom || dateTo;

  return (
    <div className="space-y-4" data-testid="admin-statistics-dashboard">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <Card className="bg-gradient-to-r from-zinc-950 via-zinc-900 to-zinc-950 border-2 border-[#C9A962]/60 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2
              className="text-2xl font-black text-white flex items-center gap-2"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.03em' }}
            >
              <BarChart3 className="h-6 w-6 text-[#FFD60A]" />
              NECS · SERVICE STATISTICS
            </h2>
            <p className="text-[11px] text-zinc-400 mt-1 max-w-2xl">
              Enterprise pivot across <strong className="text-[#C9A962]">state → region → city</strong>{' '}
              covering valet trash and every specialty service. Filter, sort and export.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={fetchStats}
              disabled={loading}
              variant="outline"
              className="border-[#C9A962]/50 text-[#C9A962] hover:bg-[#C9A962]/10 h-9"
              data-testid="admin-stats-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Refreshing…' : 'Refresh'}
            </Button>
            <Button
              onClick={exportCsv}
              disabled={!data}
              className="bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308] h-9"
              data-testid="admin-stats-export-csv"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Period selector · Day / Week / Month / Year / Custom */}
        <div
          className="flex items-center gap-1 mt-4 pt-4 border-t border-zinc-800 flex-wrap"
          data-testid="admin-stats-period-selector"
        >
          <span className="text-[10px] uppercase tracking-widest text-[#C9A962] font-bold mr-2 flex items-center gap-1">
            <Activity className="h-3 w-3" /> Snapshot Period
          </span>
          {PERIODS.map((p) => {
            const active = period === p.key;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => setPeriod(p.key)}
                className={`px-3 h-8 rounded-md text-[11px] font-bold border transition-colors ${
                  active
                    ? 'bg-[#FFD60A] text-black border-[#FFD60A]'
                    : 'bg-zinc-950 text-zinc-300 border-zinc-700 hover:bg-zinc-900'
                }`}
                data-testid={`admin-stats-period-${p.key}`}
              >
                {p.label}
              </button>
            );
          })}
          {snapshot?.period?.label && (
            <Badge className="ml-2 bg-emerald-700 text-white">
              {snapshot.period.label}
            </Badge>
          )}
        </div>

        {/* ── Big City tab strip (matches Routes & Regions style).
            Dallas · Houston · Austin · San Antonio · Tyler. Drives the
            Region column and every KPI below. */}
        <div
          className="flex items-center justify-between gap-3 flex-wrap mt-4 pt-4 border-t border-zinc-800"
          data-testid="admin-stats-bigcity-tabs"
        >
          <div className="flex items-center gap-1.5 flex-wrap">
            {['Dallas', 'Houston', 'Austin', 'San Antonio', 'Tyler'].map((c) => {
              const active = bigCity === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => { setBigCity(c); setRegionId('__ALL__'); setCity('__ALL__'); }}
                  className={`px-3.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest transition-all border ${
                    active
                      ? 'bg-[#FFD60A] text-black border-[#FFD60A] shadow-[0_0_0_1px_#FFD60A]'
                      : 'bg-zinc-950 text-zinc-300 border-zinc-700 hover:border-[#C9A962] hover:text-[#C9A962]'
                  }`}
                  data-testid={`admin-stats-bigcity-tab-${c.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {c}
                </button>
              );
            })}
          </div>
          <Badge className="bg-zinc-800 text-[#C9A962] font-mono text-[10px]" data-testid="admin-stats-scope-badge">
            Scope: {bigCity}
          </Badge>
        </div>

        {/* Filter row (kept — used with the pivot payload and, in Custom, with the snapshot) */}
        <div
          className="flex flex-wrap items-end gap-3 mt-4 pt-4 border-t border-zinc-800"
          data-testid="admin-stats-filters"
        >
          <FilterField label="State" icon={Flag}>
            <Select value={state} onValueChange={(v) => { setState(v); setRegionId('__ALL__'); setCity('__ALL__'); }}>
              <SelectTrigger className="h-8 w-[135px] bg-zinc-950 border-zinc-700 text-white text-[11px]"
                             data-testid="admin-stats-state">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 text-white max-h-72">
                <SelectItem value="__ALL__">All states</SelectItem>
                {availableStates.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                    {seededStateSet.has(s) && ' ·'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField label="Region" icon={MapPin}>
            <Select value={regionId} onValueChange={(v) => { setRegionId(v); setCity('__ALL__'); }}>
              <SelectTrigger className="h-8 w-[190px] bg-zinc-950 border-zinc-700 text-white text-[11px]"
                             data-testid="admin-stats-region">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 text-white max-h-72">
                <SelectItem value="__ALL__">All regions</SelectItem>
                {availableRegions.map((r) => (
                  <SelectItem key={r.region_id} value={r.region_id}>
                    {r.region_code} · {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField label="City" icon={Home}>
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger className="h-8 w-[170px] bg-zinc-950 border-zinc-700 text-white text-[11px]"
                             data-testid="admin-stats-city">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 text-white max-h-72">
                <SelectItem value="__ALL__">All cities</SelectItem>
                {availableCities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </FilterField>
          <FilterField label="From" icon={Calendar}>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-8 w-[135px] bg-zinc-950 border-zinc-700 text-white text-[11px]"
              data-testid="admin-stats-date-from"
            />
          </FilterField>
          <FilterField label="To" icon={Calendar}>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-8 w-[135px] bg-zinc-950 border-zinc-700 text-white text-[11px]"
              data-testid="admin-stats-date-to"
            />
          </FilterField>
          {anyFilter && (
            <Button
              variant="outline"
              className="h-8 border-zinc-700 text-zinc-300"
              onClick={() => {
                setState('__ALL__'); setRegionId('__ALL__'); setCity('__ALL__');
                setDateFrom(''); setDateTo('');
              }}
              data-testid="admin-stats-clear-filters"
            >
              Clear
            </Button>
          )}
        </div>
      </Card>

      {/* ── KPI cards ────────────────────────────────────────────────── */}
      <div
        className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2"
        data-testid="admin-stats-kpis"
      >
        <KpiCard label="Properties" value={num(totals.properties)} icon={Building2} tone="gold" testId="kpi-properties" />
        <KpiCard label="Buildings" value={num(totals.buildings)} icon={Home} testId="kpi-buildings" />
        <KpiCard label="Units" value={num(totals.units)} icon={Users} testId="kpi-units" />
        <KpiCard label="Active Regions" value={num(totals.active_regions)} icon={MapPin} testId="kpi-regions" />
        <KpiCard label="Active Cities" value={num(totals.active_cities)} icon={Home} testId="kpi-cities" />
        <KpiCard label="Signed Contracts" value={num(totals.contracts_signed)} icon={FileCheck} tone="emerald" testId="kpi-contracts" />
        <KpiCard label="Daily Reports" value={num(totals.daily_reports)} icon={ClipboardList} testId="kpi-daily" />
        <KpiCard label="Violations" value={num(totals.violations)} icon={AlertTriangle} tone="red" testId="kpi-violations" />
      </div>

      {/* ── LIVE SNAPSHOT · Operations · Billing · Training ─────────── */}
      {snapshot && (
        <div
          className={`grid grid-cols-1 ${hideBilling ? 'lg:grid-cols-2' : 'lg:grid-cols-3'} gap-3`}
          data-testid="admin-stats-live-snapshots"
        >
          <SnapshotCard
            title="Operations"
            icon={Activity}
            accent="#0ea5e9"
            periodLabel={snapshot.period?.label}
            metrics={[
              { label: 'Daily Reports', value: snapshot.operations.daily_reports },
              { label: 'Units Serviced', value: snapshot.operations.units_serviced },
              { label: 'Safety Checks', value: snapshot.operations.safety_checks },
              { label: 'Violations', value: snapshot.operations.violations_total },
              { label: 'Pending', value: snapshot.operations.violations_pending, tone: 'amber' },
              { label: 'Resolved', value: snapshot.operations.violations_resolved, tone: 'emerald' },
              { label: 'Specialty Schedules', value: snapshot.operations.specialty_schedules },
              { label: 'Active Agents', value: snapshot.operations.active_agents },
            ]}
            testId="snapshot-operations"
          />
          {!hideBilling && (
            <SnapshotCard
              title="Billing"
              icon={DollarSign}
              accent="#f59e0b"
              periodLabel={snapshot.period?.label}
              metrics={[
                { label: 'Invoices Issued', value: snapshot.billing.invoices_issued },
                { label: 'Total Invoiced', value: `$${num(snapshot.billing.total_invoiced)}` },
                { label: 'Total Collected', value: `$${num(snapshot.billing.total_collected)}`, tone: 'emerald' },
                { label: 'Outstanding A/R', value: `$${num(snapshot.billing.outstanding)}`, tone: 'red' },
                { label: 'Collection Rate', value: `${snapshot.billing.collection_rate_pct}%` },
              ]}
              statusRows={snapshot.billing.by_status}
              testId="snapshot-billing"
            />
          )}
          <SnapshotCard
            title="Training"
            icon={GraduationCap}
            accent="#a855f7"
            periodLabel={snapshot.period?.label}
            metrics={[
              { label: 'Total Modules', value: snapshot.training.total_modules },
              { label: 'Completions', value: snapshot.training.completions_in_period, tone: 'emerald' },
              { label: 'Sessions Started', value: snapshot.training.sessions_in_period },
              { label: 'Active Learners', value: snapshot.training.active_learners },
              { label: 'Total Agents', value: snapshot.training.total_agents },
              { label: 'Certification %', value: `${snapshot.training.certification_pct}%` },
            ]}
            testId="snapshot-training"
          />
        </div>
      )}

      {/* ── Per-service breakdown ────────────────────────────────────── */}
      <Card className="bg-zinc-900 border border-[#C9A962]/40 p-4" data-testid="admin-stats-service-breakdown">
        <div className="flex items-center justify-between mb-3">
          <h3
            className="text-lg font-bold text-white flex items-center gap-2"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            <Package className="h-5 w-5 text-[#FFD60A]" />
            SERVICE COVERAGE
          </h3>
          <Badge className="bg-emerald-700 text-white">
            {totals.specialty_requests ?? 0} specialty requests
          </Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-widest text-[#C9A962]">
                <th className="text-left py-2 px-2">Service</th>
                <th className="text-right py-2 px-2">Active Properties</th>
                <th className="text-right py-2 px-2">Requests / Schedules</th>
                <th className="text-right py-2 px-2">Coverage %</th>
              </tr>
            </thead>
            <tbody>
              {(data?.by_service || []).map((s) => {
                const total = totals.properties || 0;
                const pct = total ? Math.round((s.active_properties / total) * 100) : 0;
                return (
                  <tr key={s.service_type} className="border-b border-zinc-800/50 hover:bg-zinc-950/40"
                      data-testid={`svc-row-${s.service_type}`}>
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-2">
                        {s.service_type === 'valet_trash'
                          ? <Truck className="h-3.5 w-3.5 text-[#C9A962]" />
                          : <Sparkles className="h-3.5 w-3.5 text-purple-400" />}
                        <span className="text-white">{s.label || SPECIALTY_LABELS[s.service_type]}</span>
                      </div>
                    </td>
                    <td className="text-right py-2 px-2 text-white font-mono">{num(s.active_properties)}</td>
                    <td className="text-right py-2 px-2 text-zinc-300 font-mono">{num(s.requests_count)}</td>
                    <td className="text-right py-2 px-2">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-1.5 bg-zinc-800 rounded overflow-hidden">
                          <div
                            className="h-full bg-[#C9A962]"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-[11px] font-mono text-white w-9 text-right">{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {(data?.by_service || []).length === 0 && (
                <tr><td colSpan="4" className="text-center text-zinc-500 py-4 italic text-[11px]">No data.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── Regional pivot table ─────────────────────────────────────── */}
      <Card className="bg-zinc-900 border border-[#C9A962]/40 p-4" data-testid="admin-stats-region-pivot">
        <div className="flex items-center justify-between mb-3">
          <h3
            className="text-lg font-bold text-white flex items-center gap-2"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            <MapPin className="h-5 w-5 text-[#FFD60A]" />
            STATE · REGION PIVOT
          </h3>
          <Badge className="bg-zinc-700 text-white">{sortedRegions.length} rows</Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="sticky top-0 bg-zinc-900">
              <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-widest text-[#C9A962]">
                <Th label="State" col="state" sortBy={sortBy} onSort={toggleSort} />
                <Th label="Region" col="region_code" sortBy={sortBy} onSort={toggleSort} />
                <Th label="Properties" col="properties" sortBy={sortBy} onSort={toggleSort} align="right" />
                <Th label="Buildings" col="buildings" sortBy={sortBy} onSort={toggleSort} align="right" />
                <Th label="Units" col="units" sortBy={sortBy} onSort={toggleSort} align="right" />
                <Th label="Valet" col="valet_active" sortBy={sortBy} onSort={toggleSort} align="right" />
                <Th label="Specialty" col="specialty_active" sortBy={sortBy} onSort={toggleSort} align="right" />
                <Th label="Daily" col="daily_reports" sortBy={sortBy} onSort={toggleSort} align="right" />
                <Th label="Violations" col="violations" sortBy={sortBy} onSort={toggleSort} align="right" />
                <Th label="Safety" col="safety_reports" sortBy={sortBy} onSort={toggleSort} align="right" />
              </tr>
            </thead>
            <tbody>
              {sortedRegions.map((r) => (
                <tr key={r.region_id} className="border-b border-zinc-800/50 hover:bg-zinc-950/40"
                    data-testid={`pivot-region-${r.region_code}`}>
                  <td className="py-2 px-2"><Badge className="bg-zinc-800 text-white font-mono text-[10px]">{r.state}</Badge></td>
                  <td className="py-2 px-2 text-white">
                    <span className="font-mono text-[#C9A962]">{r.region_code}</span>
                    <span className="text-zinc-400 ml-2">{r.region_name}</span>
                  </td>
                  <td className="text-right py-2 px-2 text-white font-mono font-bold">{num(r.properties)}</td>
                  <td className="text-right py-2 px-2 text-zinc-300 font-mono">{num(r.buildings)}</td>
                  <td className="text-right py-2 px-2 text-zinc-300 font-mono">{num(r.units)}</td>
                  <td className="text-right py-2 px-2 text-emerald-400 font-mono">{num(r.valet_active)}</td>
                  <td className="text-right py-2 px-2 text-purple-400 font-mono">{num(r.specialty_active)}</td>
                  <td className="text-right py-2 px-2 text-zinc-300 font-mono">{num(r.daily_reports)}</td>
                  <td className="text-right py-2 px-2 text-red-400 font-mono">{num(r.violations)}</td>
                  <td className="text-right py-2 px-2 text-zinc-300 font-mono">{num(r.safety_reports)}</td>
                </tr>
              ))}
              {sortedRegions.length === 0 && (
                <tr><td colSpan="10" className="text-center text-zinc-500 py-6 italic text-[11px]">
                  No regions match the current filters.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── City breakdown ───────────────────────────────────────────── */}
      <Card className="bg-zinc-900 border border-[#C9A962]/40 p-4" data-testid="admin-stats-city-breakdown">
        <div className="flex items-center justify-between mb-3">
          <h3
            className="text-lg font-bold text-white flex items-center gap-2"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            <Home className="h-5 w-5 text-[#FFD60A]" />
            CITY BREAKDOWN
          </h3>
          <Badge className="bg-zinc-700 text-white">{(data?.by_city || []).length} cities</Badge>
        </div>
        <div className="overflow-x-auto max-h-[420px]">
          <table className="w-full text-[12px]">
            <thead className="sticky top-0 bg-zinc-900">
              <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-widest text-[#C9A962]">
                <th className="text-left py-2 px-2">State</th>
                <th className="text-left py-2 px-2">Region</th>
                <th className="text-left py-2 px-2">City</th>
                <th className="text-right py-2 px-2">Properties</th>
                <th className="text-right py-2 px-2">Units</th>
                <th className="text-right py-2 px-2">Valet</th>
                <th className="text-right py-2 px-2">Specialty</th>
                <th className="text-right py-2 px-2">Daily</th>
                <th className="text-right py-2 px-2">Violations</th>
              </tr>
            </thead>
            <tbody>
              {(data?.by_city || []).map((c) => (
                <tr key={`${c.state}-${c.region_code}-${c.city}`}
                    className="border-b border-zinc-800/50 hover:bg-zinc-950/40"
                    data-testid={`pivot-city-${c.city}`}>
                  <td className="py-2 px-2"><Badge className="bg-zinc-800 text-white font-mono text-[10px]">{c.state}</Badge></td>
                  <td className="py-2 px-2 font-mono text-[#C9A962]">{c.region_code}</td>
                  <td className="py-2 px-2 text-white">{c.city}</td>
                  <td className="text-right py-2 px-2 text-white font-mono font-bold">{num(c.properties)}</td>
                  <td className="text-right py-2 px-2 text-zinc-300 font-mono">{num(c.units)}</td>
                  <td className="text-right py-2 px-2 text-emerald-400 font-mono">{num(c.valet_active)}</td>
                  <td className="text-right py-2 px-2 text-purple-400 font-mono">{num(c.specialty_active)}</td>
                  <td className="text-right py-2 px-2 text-zinc-300 font-mono">{num(c.daily_reports)}</td>
                  <td className="text-right py-2 px-2 text-red-400 font-mono">{num(c.violations)}</td>
                </tr>
              ))}
              {(data?.by_city || []).length === 0 && (
                <tr><td colSpan="9" className="text-center text-zinc-500 py-6 italic text-[11px]">
                  No cities match the current filters.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {data?.generated_at && (
        <div className="text-[10px] text-zinc-500 text-right px-1">
          Generated at {new Date(data.generated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}

/** Live snapshot card · Operations / Billing / Training body */
function SnapshotCard({ title, icon: Icon, accent, periodLabel, metrics = [], statusRows = null, testId }) {
  return (
    <Card
      className="bg-zinc-900 border border-zinc-800 p-4"
      style={{ borderColor: `${accent}55` }}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <h3
          className="text-lg font-bold text-white flex items-center gap-2"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          <Icon className="h-5 w-5" style={{ color: accent }} />
          {title.toUpperCase()} · SNAPSHOT
        </h3>
        {periodLabel && (
          <Badge className="text-[10px]" style={{ backgroundColor: accent, color: '#000' }}>
            {periodLabel}
          </Badge>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {metrics.map((m) => (
          <SnapshotMetric key={m.label} label={m.label} value={m.value} tone={m.tone} />
        ))}
      </div>
      {statusRows && statusRows.length > 0 && (
        <div className="mt-3 pt-3 border-t border-zinc-800">
          <div className="text-[9px] uppercase tracking-widest text-[#C9A962] font-bold mb-1.5">
            By Status
          </div>
          <ul className="text-[11px] text-zinc-300 space-y-1">
            {statusRows.map((s) => (
              <li key={s.status} className="flex justify-between">
                <span className="capitalize">{s.status}</span>
                <span className="font-mono font-bold text-white">{s.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function SnapshotMetric({ label, value, tone = 'default' }) {
  const cls = {
    emerald: 'text-emerald-400',
    amber: 'text-amber-400',
    red: 'text-red-400',
    default: 'text-white',
  }[tone] || 'text-white';
  return (
    <div className="bg-zinc-950/40 border border-zinc-800 rounded p-2">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">{label}</div>
      <div className={`text-lg font-black font-mono mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}

/** Filter row field wrapper */
function FilterField({ label, icon: Icon, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[9px] uppercase tracking-widest text-[#C9A962] font-bold flex items-center gap-1">
        <Icon className="h-2.5 w-2.5" /> {label}
      </label>
      {children}
    </div>
  );
}

/** KPI stat tile */
function KpiCard({ label, value, icon: Icon, tone = 'default', testId }) {
  const toneClass = {
    gold: 'border-[#FFD60A]/50 bg-gradient-to-br from-[#C9A962]/10 to-transparent',
    emerald: 'border-emerald-500/50 bg-gradient-to-br from-emerald-500/10 to-transparent',
    red: 'border-red-500/50 bg-gradient-to-br from-red-500/10 to-transparent',
    default: 'border-zinc-800 bg-zinc-950/40',
  }[tone];
  return (
    <Card className={`p-3 ${toneClass}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{label}</span>
        <Icon className="h-3.5 w-3.5 text-[#C9A962]" />
      </div>
      <div className="text-2xl font-black text-white mt-1 font-mono">{value}</div>
    </Card>
  );
}

/** Sortable table header cell */
function Th({ label, col, sortBy, onSort, align = 'left' }) {
  const active = sortBy.col === col;
  return (
    <th
      onClick={() => onSort(col)}
      className={`py-2 px-2 cursor-pointer select-none hover:text-white ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${active ? 'text-white' : ''}`}
      data-testid={`sort-col-${col}`}
    >
      <span className={`inline-flex items-center gap-0.5 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        {active && (sortBy.dir === 'asc'
          ? <ChevronUp className="h-3 w-3" />
          : <ChevronDown className="h-3 w-3" />)}
      </span>
    </th>
  );
}
